import React, { useMemo, useState } from "react";
import { requiresMandatoryLOs } from "../../utils/completionCriteria";
import { Box, Typography, useTheme } from "@mui/material";
import { AlertTriangle, SquarePen } from "lucide-react";
import { useTranslation } from "react-i18next";
import BannerCst from "@components/BannerCst";
import { useWizardReadOnly } from "@components/WizardCst";
import { WizardStepProps } from "@components/WizardCst/types";
import LOPickerCst, { LOPickerSelectedItem } from "@components/LOPickerCst";
import SelectedLOList from "@components/SelectedLOList";
import LOAlternativesPickerModal from "../../components/LOAlternativesPickerModal";
import ContentActionsBarCst from "../../components/ContentActionsBarCst";
import CreateActivityModalCst, {
  CreatedDraftLo,
} from "../../components/CreateActivityModalCst";
import {
  AlternativeLOSection,
  LOCompletionCriteria,
  SelectedLOItem,
} from "../../types";
import {
  flattenSelectedLOItems,
  removeLOFromSection,
} from "../../utils/loStructure";
import { CourseWizardData, SelectedActivity } from "../types";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { ButtonTertiary } from "src/UI/ButtonTertiary";

interface AddActivitiesStepProps extends WizardStepProps<CourseWizardData> {
  lockAdditions?: boolean;
  lockReorder?: boolean;
}

const AddActivitiesStep: React.FC<AddActivitiesStepProps> = ({
  data,
  setData,
  lockAdditions = false,
  lockReorder = false,
}) => {
  const { t } = useTranslation("wizard-course");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const theme = useTheme();
  const readOnly = useWizardReadOnly();
  const additionsDisabled = lockAdditions || readOnly;
  const completionCriteriaEnabled = requiresMandatoryLOs(
    data.completion_criterion,
  );

  const [activityPickerOpen, setActivityPickerOpen] = useState(false);
  const [alternativeSectionIndex, setAlternativeSectionIndex] = useState<
    number | "new" | null
  >(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const activities = data.selected_activities;
  const selectedActivities = useMemo(
    () => flattenSelectedLOItems(activities),
    [activities],
  );
  const count = selectedActivities.length;
  const isEmpty = count === 0;
  const hasCreatedContent = selectedActivities.some(
    (item) => item.entity?.is_draft,
  );
  const counters = isEmpty
    ? []
    : [String(t("step-activities.counter", { count }))];

  const excludeIds = useMemo(
    () => selectedActivities.map((activity) => activity.id),
    [selectedActivities],
  );

  const handlePickerConfirm = (added: LOPickerSelectedItem[]) => {
    if (added.length === 0) return;

    const known = new Set(excludeIds);
    const fresh: SelectedActivity[] = added
      .filter((a) => !known.has(a.id))
      .map((a) => ({
        id: a.id,
        title: a.title,
        type: a.type,
        kind: "activity",
        entity: a.entity,
        completion_criteria: null,
      }));
    if (fresh.length === 0) return;

    setData({ selected_activities: [...activities, ...fresh] });
  };

  const alternativeSection = useMemo(() => {
    if (typeof alternativeSectionIndex !== "number") return null;
    const item = activities[alternativeSectionIndex];
    return item?.kind === "section" ? item : null;
  }, [activities, alternativeSectionIndex]);

  const alternativeExcludeIds = useMemo(() => {
    if (typeof alternativeSectionIndex !== "number") return excludeIds;
    return flattenSelectedLOItems(
      activities.filter((_, index) => index !== alternativeSectionIndex),
    ).map((item) => item.id);
  }, [activities, alternativeSectionIndex, excludeIds]);

  const handleAlternativeConfirm = (
    section: AlternativeLOSection<SelectedActivity>,
  ) => {
    if (!completionCriteriaEnabled) return;
    if (alternativeSectionIndex === "new") {
      setData({ selected_activities: [...activities, section] });
    } else if (typeof alternativeSectionIndex === "number") {
      if (activities[alternativeSectionIndex]?.locked) return;
      setData({
        selected_activities: activities.map((activity, index) =>
          index === alternativeSectionIndex ? section : activity,
        ),
      });
    }
    setAlternativeSectionIndex(null);
  };

  const handleRemoveSection = (sectionIndex: number) => {
    if (activities[sectionIndex]?.locked) return;
    setData({
      selected_activities: activities.filter(
        (_, index) => index !== sectionIndex,
      ),
    });
  };

  const handleRemove = (id: number) => {
    setData({
      selected_activities: activities.filter(
        (activity) => activity.kind === "section" || activity.id !== id,
      ),
    });
  };

  const handleRemoveFromSection = (sectionIndex: number, id: number) => {
    if (activities[sectionIndex]?.locked) return;
    setData({
      selected_activities: removeLOFromSection(activities, sectionIndex, id),
    });
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    const next = [...activities];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setData({ selected_activities: next });
  };

  const handleCreated = (item: CreatedDraftLo) => {
    if (excludeIds.includes(item.id)) return;
    const fresh: SelectedActivity = {
      id: item.id,
      title: item.title,
      type: item.type,
      kind: "activity",
      entity: item.entity,
      completion_criteria: null,
    };
    setData({ selected_activities: [...activities, fresh] });
  };

  // TODO: pagina dettaglio v2 attività non disponibile oggi.
  const handleDetails = (id: number) => {
    console.info("[course-wizard] activity details — TODO", id);
  };

  const handleCompletionCriteriaChange = (
    item: SelectedLOItem,
    value: LOCompletionCriteria,
  ) => {
    if (!completionCriteriaEnabled) return;
    setData({
      selected_activities: activities.map((activity) =>
        activity.kind !== "section" && activity.id === item.id
          ? { ...activity, completion_criteria: value }
          : activity,
      ),
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {hasCreatedContent && (
        <BannerCst type="warning" label={t("step-activities.publish-banner")} />
      )}

      {isEmpty && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: "12px 16px",
            borderRadius: "12px",
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.dark,
            border: `1px solid ${theme.palette.warning.main}`,
          }}
        >
          <AlertTriangle size={20} />
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: theme.palette.common.black,
            }}
          >
            {t("step-activities.empty-warning")}
          </Typography>
        </Box>
      )}

      <ContentActionsBarCst isEmpty={isEmpty} counters={counters}>
        <ButtonPrimary
          size="md"
          onClick={() => setActivityPickerOpen(true)}
          disabled={additionsDisabled}
        >
          {t("step-activities.add")}
        </ButtonPrimary>
        {completionCriteriaEnabled && (
          <ButtonSecondary
            size="md"
            onClick={() => setAlternativeSectionIndex("new")}
            disabled={additionsDisabled}
          >
            {t("step-activities.add-alternative")}
          </ButtonSecondary>
        )}
        {isEmpty ? (
          <ButtonSecondary
            size="md"
            onClick={() => setCreateModalOpen(true)}
            disabled={additionsDisabled}
          >
            {t("step-activities.create-new")}
          </ButtonSecondary>
        ) : (
          <ButtonTertiary
            size="md"
            onClick={() => setCreateModalOpen(true)}
            startIcon={<SquarePen size={18} />}
            disabled={additionsDisabled}
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              "&:hover": {
                color: theme.palette.primary.main,
                textDecoration: "none",
              },
            }}
          >
            {t("step-activities.create-new")}
          </ButtonTertiary>
        )}
      </ContentActionsBarCst>

      {!isEmpty && (
        <SelectedLOList
          items={activities}
          tCatalog={tCatalog}
          removeLabel={t("step-activities.remove")}
          onRemove={handleRemove}
          onRemoveFromSection={handleRemoveFromSection}
          onEditSection={lockAdditions ? undefined : setAlternativeSectionIndex}
          onRemoveSection={lockAdditions ? undefined : handleRemoveSection}
          onDetails={(item) => handleDetails(item.id)}
          onReorder={handleReorder}
          reorderDisabled={lockReorder}
          // showDetailsAction
          completionCriteriaEnabled={completionCriteriaEnabled}
          onCompletionCriteriaChange={handleCompletionCriteriaChange}
        />
      )}

      <LOPickerCst
        open={activityPickerOpen}
        onClose={() => setActivityPickerOpen(false)}
        title={t("picker-activities.title")}
        entityType="activity"
        excludeIds={excludeIds}
        onConfirm={handlePickerConfirm}
      />

      <LOAlternativesPickerModal<SelectedActivity>
        open={completionCriteriaEnabled && alternativeSectionIndex !== null}
        mode="course"
        section={alternativeSection}
        excludeIds={alternativeExcludeIds}
        onClose={() => setAlternativeSectionIndex(null)}
        onConfirm={handleAlternativeConfirm}
      />

      <CreateActivityModalCst
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={handleCreated}
      />
    </Box>
  );
};

export default AddActivitiesStep;
